<template>
  <div class="app-breadcrumb">
    <UIDropdownSearchList
      :data
      :current-value
      size="xs"
      placement="bottom-start"
      popper-class="app-breadcrumb__dropdown"
      @click:item="handleClickItem"
    >
      <template #default="{ toggle }">
        <UIButtonIcon
          v-tooltip.top="'Filter analytics'"
          :leading="FunnelIcon"
          variant="secondary"
          size="xxs"
          @click="toggle"
        />
      </template>
    </UIDropdownSearchList>
    <div class="app-breadcrumb__current">
      {{ displayValue }}
    </div>
    <UIButtonIcon
      v-if="isLockButtonVisible"
      v-tooltip.top="isLocked ? 'Unlock the current path' : 'Lock the current path'"
      :leading="isLocked ? LockClosedIcon : LockOpenIcon"
      variant="gray"
      fill="light"
      size="xxs"
      class="app-breadcrumb__lock"
      @click="handleLockCurrentPath"
    />
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { orderBy, sortedUniqBy } from 'lodash'
import { useRoute } from 'vue-router'

import { ASSET_FIELD, ROUTE_NAME, UNKNOWN_VALUE } from '@/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepositoriesStore } from '@/store/repositories/index'

import { UIButtonIcon } from '@ui/buttons'
import { UIDropdownSearchList } from '@ui/dropdowns'
import { FunnelIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/vue/24/outline'
import { AnalyticsField } from '@types'

type Props = {
  items: string[][]
  path: string[]
}

type Emits = {
  'click:item': [data: string[]]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'AppBreadcrumb',
})

const assetsBunchStore = useAssetsBunchStore()
const repositoriesStore = useRepositoriesStore()

const route = useRoute()

const inputItems = computed(
  () =>
    props.items?.map(item => ({
      value: JSON.stringify(item),
      label: getAssetName(item.at(-1)),
    })) || [],
)

const data = computed(() =>
  sortedUniqBy(
    orderBy(
      inputItems.value,
      item => item.label === repositoriesStore.getCurrentName || item.label,
    ),
    item => item.label.at(-1),
  ),
)

const currentNode = computed(() => props.path.at(-1))

const displayValue = computed(() => getAssetName(currentNode.value))

const currentValue = computed(() => {
  if (!currentNode.value) return
  return inputItems.value.find(
    item => JSON.parse(item.value).at(-1) === currentNode.value,
  )?.value
})

const isLockButtonVisible = computed(() => {
  return route.name === ROUTE_NAME.DASHBOARDS_DASHBOARD
})

const currentDashboardKey = computed(() => route.params.slug as string)

const settingsKey = computed(() => `dashboards.${currentDashboardKey.value}.path`)

const isLocked = computed(() => {
  const settings = repositoriesStore.getCurrentUISettings
  return JSON.stringify(settings?.dashboards?.[currentDashboardKey.value]?.path) === JSON.stringify(props.path)
})

const getAssetName = (id?: string) => {
  if (!id) return UNKNOWN_VALUE
  if (id === AnalyticsField.REPO_ROOT)
    return repositoriesStore.getCurrentName || UNKNOWN_VALUE
  const asset = assetsBunchStore.getElementById(id)
  return asset?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}

const handleClickItem = (value?: string) => {
  if (!value) return
  if (isLocked.value) {
    lockCurrentPath(JSON.parse(value))
  } else {
    if (value === JSON.stringify(props.path)) return
    emit('click:item', JSON.parse(value))
  }
}

const lockCurrentPath = (value: string[]) => {
  repositoriesStore.setCurrentUISettings([[settingsKey.value, value]])
}

const unlockCurrentPath = () => {
  repositoriesStore.setCurrentUISettings([[settingsKey.value, undefined]])
}

const handleLockCurrentPath = () => {
  if (!isLockButtonVisible.value) return
  if (isLocked.value) {
    unlockCurrentPath()
  } else {
    lockCurrentPath(props.path)
  }
}
</script>

<style>
.app-breadcrumb {
  @apply flex flex-auto items-center;
  @apply gap-2;

  &__current {
    @apply text-base sm:text-sm;
    @apply truncate;
  }

  &__dropdown {
    .v-popper__inner {
      @apply min-w-56;
    }
  }

  &__lock {
    @apply -ml-1;
  }
}
</style>
